import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { func, node, number, oneOf, string } from "prop-types";
import uuid from "uuid/v1";

import positions from "../constants/positions";
import bestPositionForTooltip from "../utilities/bestPositionForTooltip";
import * as S from "./styled";

const BasicTooltip = forwardRef(
  (
    {
      children,
      className,
      content,
      onTooltipOpen,
      position: preferredPosition,
      zIndex
    },
    ref
  ) => {
    const [isContentVisible, setIsContentVisible] = useState(false);
    const [position, setPosition] = useState(preferredPosition);

    const ariaLabelRef = useRef(uuid());
    const tooltipRef = useRef();
    const triggerRef = useRef();

    useEffect(() => {
      if (triggerRef.current?.firstChild?.tagName !== "BUTTON") {
        // eslint-disable-next-line no-console
        console.error(
          "The element you pass to BasicTooltip as a trigger must be a button"
        );
      }
    }, [triggerRef]);

    /**
     * spacer represents the distance between the tooltip and the tooltip trigger
     * in px. This value is used for styling and in calculations related to the
     * tooltip's best fit.
     */
    const spacer = 10;

    const tooltipPosition = useCallback(
      () =>
        bestPositionForTooltip({
          preferredPosition,
          spacer,
          tooltipElem: tooltipRef.current,
          triggerElem: triggerRef.current
        }),
      [position, preferredPosition]
    );

    const showContent = () => {
      // eslint-disable-next-line no-unused-expressions
      onTooltipOpen?.();
      setPosition(tooltipPosition());
      setIsContentVisible(true);
    };

    const hideContent = () => {
      setIsContentVisible(false);
    };

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        hideContent();
      }
      if (event.key === "Enter") {
        showContent();
      }
    };

    const TriggerNode = React.cloneElement(children, {
      ...children.props,
      "aria-labelledby": ariaLabelRef.current,
      onBlur: event => {
        // eslint-disable-next-line no-unused-expressions
        children.props.onBlur?.(event);
        hideContent();
      },
      onFocus: event => {
        // eslint-disable-next-line no-unused-expressions
        children.props.onFocus?.(event);
        showContent();
      },
      onKeyDown: event => {
        // eslint-disable-next-line no-unused-expressions
        children.props.onKeyDown?.(event);
        handleKeyDown(event);
      },
      onMouseEnter: event => {
        // eslint-disable-next-line no-unused-expressions
        children.props.onMouseEnter?.(event);
        showContent();
      },
      onMouseLeave: event => {
        // eslint-disable-next-line no-unused-expressions
        children.props.onMouseLeave?.(event);
        hideContent();
      },
      type: "button"
    });

    return (
      <S.TooltipContainer ref={ref} className={className}>
        <span ref={triggerRef}>{TriggerNode}</span>
        <S.TooltipContent
          ref={tooltipRef}
          id={ariaLabelRef.current}
          isVisible={isContentVisible}
          position={position}
          role="tooltip"
          spacer={spacer}
          zIndex={zIndex}
        >
          <S.TooltipText>{content}</S.TooltipText>
        </S.TooltipContent>
      </S.TooltipContainer>
    );
  }
);

BasicTooltip.propTypes = {
  className: string,
  /**
   * Determines the element which will act as the tooltip trigger.
   *
   * Due to accessibility considerations, children must be one of the following:
   *
   * 1. An HTML Button element
   * 2. A React component that returns a button as its first child
   *
   * Note: if passing a functional component as a child, make sure that it
   * can receive any props passed to it:
   *
   * ```
   * const MyTooltipTrigger = props => <button {...props}><SaveIcon /></button>;
   *
   * const MyTooltip = <BasicTooltip content="Save Listing"><MyTooltipTrigger /></BasicTooltip>;
   * ```
   *
   */
  children: node.isRequired,
  /**
   * Determines the text which the tooltip shows when open.
   *
   * It is recommended that the string passed to the content prop be 20 characters
   * or less.
   *
   * */
  content: string.isRequired,
  /**
   * A function executed when BasicTooltip is opened.
   * */
  onTooltipOpen: func,
  /**
   * Determines where the tooltip is rendered relative to the position of its
   * trigger.
   *
   * Valid position values are accessed using the `tooltipPositions` enum like so:
   *
   * ```
   * import { BasicTooltip, tooltipPositions } from "@streeteasy/design-system/tooltip";
   *
   * const MyTooltip = (<BasicTooltip
   *                      content="I render to the left"
   *                      position={tooltipPositions.LEFT}
   *                    >
   *                      <TooltipTrigger />
   *                    </BasicTooltip>);
   * ```
   *
   * If the tooltip does not fit in the position passed as a prop, the tooltip will
   * attempt to find a better position, searching clockwise: top, right, bottom, left.
   *
   * If no suitable position is available, the tooltip will render in the position
   * passed as a prop.
   *
   * */
  position: oneOf(Object.values(positions)),
  /**
   * A number representing the z-index attribute of the tooltip.
   */
  zIndex: number
};

BasicTooltip.defaultProps = {
  className: "",
  onTooltipOpen: null,
  position: positions.TOP,
  zIndex: 1
};

export default BasicTooltip;
